"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import autoWertChart from "../../../assets/auto-bewerten/auto-bewertung-preis-verlauf.webp";
import grausesAutoSecondary from "@/assets/fahrgestellnummer-pruefen/weißes-auto.png";

import grausesAuto from "../../../assets/auto-bewerten/graues-auto.png";
import headerBgDesktop from "@/assets/auto-bewerten/Header-hintergund.png";
import headerBgMobile from "@/assets/auto-bewerten/Header-hintergund-mobile.png";
import ButtonAnimated from "@/components/ButtonAnimated/ButtonAnimated";
import Image from "next/image";
import Input from "@/common/Input";
import { CarTopInfo } from "@/common/SelectionChips";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/ContextProvider";
import { createVehicleFlow } from "@/app/helpers/vehicleStepFlow";
import { saveAnmeldenFlowPayload } from "@/app/anmelden/anmeldenFlowPayload";
import trackVehicleCreated from "@/lib/trackVehicleCreated";

export const TopSection = ({
  title = "Gratis Auto Bewerten",
  smallTitle,
  description,
  secondaryCar = false,
  useCustomImage = false,
  step,
  setStep,
  // **Wichtig**: selectedBrand, selectedModel, selectedBauform usw. sind **Objekte** {id, name}
  selectedBrand,
  setSelectedBrand,
  selectedModel,
  setSelectedModel,
  selectedYear,
  setSelectedYear,
  selectedBauform,
  setSelectedBauform,
  selectedFuel,
  setSelectedFuel,
  selectedVariant,
  setSelectedVariant,
  selectedPs,
  setSelectedPs,
  selectedTransmission,
  setSelectedTransmission,
  selectedDoors,
  setSelectedDoors,
  selectedModelType,
  setSelectedModelType,
  selectedKmStand,
  setSelectedKmStand,
}) => {
  const { userData, isAnalyticalChecked } = useAuth();
  const api = useBackendUrl();

  const router = useRouter();
  const showToastDeferred = useMemo(
    () =>
      (message, type = "error") => {
        import("@/app/helpers/showToast").then(({ showToast }) =>
          showToast(message, type),
        );
      },
    [],
  );

  useEffect(() => {
    if (step !== 11) return;
    router.prefetch("/anmelden");
  }, [router, step]);

  // Animation-States
  const [animateOut, setAnimateOut] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false); // verhindert Doppelklick
  // is-hidden waehrend des Step-Swaps (wie Startseite: swapInvisible) -> verhindert
  // das eine-Frame-Aufblitzen des Subtitles beim Wechsel Subtitle -> Chips.
  const [swapInvisible, setSwapInvisible] = useState(false);

  // Roh-Daten für die Dropdowns
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [bauformen, setBauformen] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [psOptions, setPsOptions] = useState([]);
  const [gearboxOptions, setGearboxOptions] = useState([]);
  const [doorOptions, setDoorOptions] = useState([]);
  const [modelTypeOptions, setModelTypeOptions] = useState([]);

  // Aktuelle Eingabe-Strings (Autocomplete-Input)
  const [brandInput, setBrandInput] = useState("");
  const [modelInput, setModelInput] = useState("");
  const [yearInput, setYearInput] = useState("");
  const [bauformInput, setBauformInput] = useState("");
  const [fuelInput, setFuelInput] = useState("");
  const [variantInput, setVariantInput] = useState("");
  const [psInput, setPsInput] = useState("");
  const [transmissionInput, setTransmissionInput] = useState("");
  const [doorsInput, setDoorsInput] = useState("");
  const [modelTypeInput, setModelTypeInput] = useState("");
  const [kmStandInput, setKmStandInput] = useState("");
  const [inputError, setInputError] = useState("");
  // Fehlermeldung beim Step-Wechsel zuruecksetzen (nicht am falschen Feld haengen lassen)
  useEffect(() => {
    setInputError("");
  }, [step]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [filteredItemsStep, setFilteredItemsStep] = useState(0);

  const setFilteredForStep = (items, stepIndex) => {
    setFilteredItems(items);
    setFilteredItemsStep(stepIndex);
  };

  // ----------------------------------------
  // 2) Animation Helpers
  // ----------------------------------------
  // Ziel-Step + Fade-Status als Refs (wie Startseite): erlaubt "Fade zuerst" beim
  // Zurueck (getBackStep ist async) ohne Flackern.
  const nextStepRef = useRef(null);
  const fadeOutDoneRef = useRef(false);

  const handleFadeOut = () => {
    fadeOutDoneRef.current = false;
    setAnimateOut(true);
    setIsAnimating(true);
  };

  // Step-Swap + Fade-In ausloesen, sobald das Ziel feststeht.
  // WICHTIG (wie Startseite): waehrend des Swaps wird der Slot unsichtbar
  // geschaltet (swapInvisible). Sonst entfernt setAnimateOut(false) die
  // fade-out-Klasse einen Frame BEVOR CarTopInfo intern auf die Chips
  // umschaltet -> der Subtitle blitzt eine Frame lang bei voller Deckkraft auf
  // ("Text -> Marke -> Text -> Marke"-Flackern beim allerersten Schritt).
  const ensureFadeInFor = (target) => {
    setAnimateOut(false);
    setSwapInvisible(true); // 👈 Slot verstecken, solange wir den Step tauschen
    setStep(target);
    nextStepRef.current = target; // Reveal-Marker bis der Gatekeeper feuert
    fadeOutDoneRef.current = false;
  };

  // Gatekeeper (wie Startseite): blendet erst ein, wenn der neue Step committed
  // UND der Slot versteckt ist. CarTopInfo hat im selben Passive-Effect-Flush
  // bereits auf die Chips umgeschaltet -> kein Aufblitzen des alten Subtitles.
  useEffect(() => {
    if (!swapInvisible) return;
    if (nextStepRef.current == null) return;
    setAnimateIn(true);
    setSwapInvisible(false);
    nextStepRef.current = null;
  }, [swapInvisible, step]);

  const onFadeOutEnd = (e) => {
    if (e.nativeEvent.animationName !== "fadeOut") return;
    // animateOut bleibt true, bis das (ggf. async) Ziel feststeht -> der
    // forwards-Fill der Fade-Out haelt opacity 0, kein Aufblitzen des alten Steps.
    fadeOutDoneRef.current = true;
    if (nextStepRef.current != null) {
      ensureFadeInFor(nextStepRef.current);
    }
    // sonst: Ziel-Step (async Zurueck) noch nicht da -> handleBack ruft ensureFadeInFor
  };

  const onFadeInEnd = (e) => {
    if (e.nativeEvent.animationName !== "fadeIn") return;
    setAnimateIn(false);
    setIsAnimating(false);
  };

  // ----------------------------------------
  // 3) Input-Handling (Autocomplete):
  //    - Wir schreiben INSIDE handleInput/handleSelect immer **Objekte** {id,name} in die States
  //    - Im Input-Feld (text) selber zeigen wir nur die `name`-Eigenschaft
  // ----------------------------------------
  const brandAliases = {
    vw: "Volkswagen",
  };
  const handleInput = (
    val,
    options,
    setInput,
    setFiltered,
    setSelectedObj,
    currentStep,
  ) => {
    const raw = val.trim();
    const lower = raw.toLowerCase();
    setInput(val);

    let filtered;
    // → SPEZIALFALL: nur im Step 1 (Marke) und exakt "vw"
    if (currentStep === 1 && lower === "vw") {
      filtered = options.filter(
        (o) => o.name.toLowerCase() === brandAliases.vw.toLowerCase(),
      );
    } else {
      // normales StartsWith-Filtering für alle anderen Fälle
      filtered = options.filter((o) => o.name.toLowerCase().startsWith(lower));
    }

    setFiltered(filtered);

    // Sonderfall: kmStand (letzter Schritt) – kein Lookup, direkt setzen
    if (currentStep === 11) {
      setSelectedKmStand(val.trim());
      return;
    }

    // 2) Exaktes Matching: wenn `val` genau dem `name` eines Objekts entspricht,
    //    setzen wir dieses Objekt sofort – sowohl Name für das Input-Feld, als auch id intern
    const exactMatch = options.find(
      (o) => o.name.toString().toLowerCase() === val.trim().toLowerCase(),
    );
    if (exactMatch) {
      // ➡️ Wir übergeben das ganze Objekt {id,name}
      setSelectedObj(exactMatch);
    } else {
      // Wenn keine Übereinstimmung, wieder auf leeren Wert zurücksetzen
      setSelectedObj({ id: "", name: "" });
    }
  };

  const handleSelect = (valObj, setInput, setSelectedObj, setFiltered) => {
    // `valObj` ist garantiert ein Objekt {id,name}
    setInput(valObj.name);
    setSelectedObj(valObj);
    setFiltered([]);
  };

  const optionSettersByStep = useMemo(
    () => [
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ],
    [
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ],
  );

  const allSetInputs = useMemo(
    () => [
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ],
    [
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ],
  );

  const allSetSelected = useMemo(
    () => [
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ],
    [
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ],
  );

  const vehicleFlow = useMemo(
    () =>
      createVehicleFlow({
        api,
        setOptionsByStep: optionSettersByStep,
        setInputsByStep: allSetInputs,
        setSelectedByStep: allSetSelected,
        setFilteredItems,
        showToast: showToastDeferred,
        alwaysSkipVehicleSteps: [10],
      }),
    [
      api,
      optionSettersByStep,
      allSetInputs,
      allSetSelected,
      setFilteredItems,
      showToastDeferred,
    ],
  );

  useEffect(() => {
    vehicleFlow.loadBrands();
  }, [vehicleFlow]);

  useEffect(() => {
    setFilteredForStep([], step);
  }, [step]);

  const handleManualAdd = async () => {
    /* 1) Kilometerstand prüfen ------------------------------------ */
    if (!selectedKmStand) {
      showToastDeferred("Kein Kilometerstand angegeben", "error");
      return;
    }

    /* 2) Gastmodus → nur UI-Step wechseln -------------------------- */
    if (!userData) {
      const pagePath =
        typeof window !== "undefined"
          ? window.location.pathname
          : "/auto-bewerten";

      saveAnmeldenFlowPayload({
        title: "Bitte gib deine E-Mail-Adresse ein, um fortzufahren",
        fin: null,
        isManual: true,
        marke: selectedBrand,
        model: selectedModel,
        baujahr: selectedYear,
        bodytype: selectedBauform,
        fueltype: selectedFuel,
        variant: selectedVariant,
        ps: selectedPs,
        gearbox: selectedTransmission,
        doors: selectedDoors,
        modelType: selectedModelType,
        kmStand: selectedKmStand,
        page: pagePath,
      });

      router.push("/anmelden?fromFlow=1");
      return;
    }

    /* 3) Payload zusammenstellen ---------------------------------- */
    const payload = {
      email: userData.email,
      kmStand: selectedKmStand,
      marke: selectedBrand.name,
      model: selectedModel.name,
      gearbox: selectedTransmission.name,
      fueltype: selectedFuel.name,
      bodytype: selectedBauform.name,
      baujahr: selectedYear.name,
      doors: selectedDoors.id,
      ps: selectedPs.name,
      variant: selectedVariant?.name ?? "",
      type: selectedModelType?.name ?? "",
      vehicleDataIds: {
        brand: selectedBrand?.id ?? null,
        model: selectedModel?.id ?? null,
        year: selectedYear?.id ?? null,
        body: selectedBauform?.id ?? null,
        fuel: selectedFuel?.id ?? null,
        variant: selectedVariant?.id ?? selectedVariant?.name ?? null,
        kw: selectedPs?.id ?? null,
        gearbox: selectedTransmission?.id ?? null,
        doors: selectedDoors?.id ?? null,
        modelType: selectedModelType?.id ?? null,
      },
      salesdate: "2025-06-01",
    };

    try {
      /* 4) POST /user/add-vehicle-manually -------------------------- */
      const res = await fetch(`${api}/user/add-vehicle-manually`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (!res.ok) throw new Error(json.error || "Fehler beim Anlegen");

      trackVehicleCreated({ vehicleId: json.id, entrypoint: "auto-bewerten" });

      const priceCalculationAutomated =
        json?.ab?.priceCalculationAutomated === true ||
        json?.priceCalculationAutomated === true;
      const targetRoute = priceCalculationAutomated
        ? `/status/${json.id}`
        : json.vehicleMatchFound
          ? `/preis/${json.id}`
          : `/status/${json.id}`;

      router.push(targetRoute);
    } catch (err) {
      showToastDeferred(
        err.message === "Failed to fetch" ? "Netzwerkfehler" : err.message,
        "error",
      );
      console.error(err);
    }
  };

  // ----------------------------------------
  // 5) Haupt-Handler für „Weiter“ (inkl. Auto-Skip)
  // ----------------------------------------
  const handleNext = async () => {
    if (isAnimating) return;
    setFilteredForStep([], step);

    if (
      userData &&
      Array.isArray(userData.fins) &&
      userData.fins.length >= 10
    ) {
      showToastDeferred(
        "Du hast die maximale Anzahl an Fahrzeugen in deinem Account erreicht. Entferne eines, um dieses Fahrzeug hinzuzufügen.",
        "error",
      );
      return;
    }

    if (step >= 11) {
      if (!selectedKmStand) {
        return showToastDeferred("Kein Kilometerstand angegeben", "error");
      }
      handleManualAdd();
      return;
    }

    // Leeres/ungueltiges Feld -> Fehlermeldung am Input (Referenz-Stil), nicht weiter.
    // Gueltig, wenn die Auswahl eine id ODER einen name hat (Flow nutzt z. B. fuer
    // Variante den Namen) -> blockiert nichts Valides.
    const currentSelection = [
      selectedBrand,
      selectedModel,
      selectedYear,
      selectedBauform,
      selectedFuel,
      selectedVariant,
      selectedPs,
      selectedTransmission,
      selectedDoors,
      selectedModelType,
    ][step - 1];
    if (!(currentSelection && (currentSelection.id || currentSelection.name))) {
      setInputError(
        {
          1: "Bitte gib eine gültige Automarke ein",
          2: "Bitte gib ein gültiges Modell ein",
          3: "Bitte wähle ein Baujahr",
          4: "Bitte wähle eine Bauform",
          5: "Bitte wähle einen Kraftstoff",
          6: "Bitte wähle eine Variante",
          7: "Bitte wähle den PS-Wert",
          8: "Bitte wähle ein Getriebe",
          9: "Bitte wähle die Türenanzahl",
          10: "Bitte wähle einen Modeltyp",
        }[step] || "Bitte triff eine gültige Auswahl",
      );
      return;
    }

    const target = await vehicleFlow.getNextStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });

    if (target === "STOP" || target == null) return;

    nextStepRef.current = target;
    handleFadeOut();
  };

  // Zurueck-Navigation (wie Startseite): getBackStep raeumt die Auswahl ab
  // (resetFromStep), danach Fade-Out -> Step-Swap -> Fade-In ueber dieselbe
  // Mechanik wie handleNext.
  const handleBack = async () => {
    if (isAnimating) return;
    setFilteredForStep([], step);
    if (step <= 1) return;

    // Fade ZUERST starten, damit getBackStep die Auswahl waehrend des Ausblendens
    // loescht (CarTopInfo behaelt die Chips via animateOut-Guard) -> kein Flackern.
    handleFadeOut();
    // Back-Ziel frueh registrieren (Step 2 -> 1), damit shouldAnimateInfo stimmt
    // und der Chip sauber raus-fadet, bevor getBackStep die Marke loescht.
    if (step === 2) nextStepRef.current = 1;

    const found = await vehicleFlow.getBackStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });
    if (found == null || found === "STOP") {
      // kein Ziel -> Fade abbrechen
      setAnimateOut(false);
      setIsAnimating(false);
      fadeOutDoneRef.current = false;
      nextStepRef.current = null;
      return;
    }
    nextStepRef.current = found;
    if (fadeOutDoneRef.current) ensureFadeInFor(found);
  };

  // Step-Labels + Fortschritt + Auswahl-Chips (wie auf der Startseite)
  const headers = [
    "",
    "Deine Automarke",
    "Dein Modell",
    "Baujahr",
    "Bauform",
    "Kraftstoff",
    "Variante",
    "PS-Wert",
    "Getriebe",
    "Türen",
    "Modeltyp",
    "Kilometerstand",
  ];
  const maxStep = headers.length - 1;
  const percentage =
    step > 0 ? (step === maxStep ? 99 : Math.round((step / maxStep) * 100)) : 0;

  const selectedChips = useMemo(() => {
    const arr = [];
    if (selectedBrand?.name)
      arr.push({ key: "brand", label: "Marke", value: selectedBrand.name });
    if (selectedModel?.name)
      arr.push({ key: "model", label: "Modell", value: selectedModel.name });
    if (selectedYear?.name)
      arr.push({ key: "year", label: "Baujahr", value: selectedYear.name });
    if (selectedBauform?.name)
      arr.push({ key: "bau", label: "Bauform", value: selectedBauform.name });
    if (selectedFuel?.name)
      arr.push({ key: "fuel", label: "Kraftstoff", value: selectedFuel.name });
    if (selectedVariant?.name)
      arr.push({ key: "var", label: "Variante", value: selectedVariant.name });
    if (selectedPs?.name)
      arr.push({ key: "ps", label: "PS", value: selectedPs.name });
    if (selectedTransmission?.name)
      arr.push({
        key: "gb",
        label: "Getriebe",
        value: selectedTransmission.name,
      });
    if (selectedDoors?.id)
      arr.push({
        key: "doors",
        label: "Türen",
        value: String(selectedDoors.id),
      });
    if (selectedModelType?.name)
      arr.push({ key: "mt", label: "Modeltyp", value: selectedModelType.name });
    if (selectedKmStand)
      arr.push({
        key: "km",
        label: "Kilometer",
        value: String(selectedKmStand),
      });
    return arr.slice(0, Math.max(0, step - 1));
  }, [
    step,
    selectedBrand?.name,
    selectedModel?.name,
    selectedYear?.name,
    selectedBauform?.name,
    selectedFuel?.name,
    selectedVariant?.name,
    selectedPs?.name,
    selectedTransmission?.name,
    selectedDoors?.id,
    selectedModelType?.name,
    selectedKmStand,
  ]);

  // ----------------------------------------------------
  // Mapping-Arrays für Autocomplete-Komponente (1–11)
  // ----------------------------------------------------
  const allOptions = [
    brands,
    models,
    years,
    bauformen,
    fuels,
    variants,
    psOptions,
    gearboxOptions,
    doorOptions,
    modelTypeOptions,
    [], // kmStand → kein Dropdown
  ];
  const allInputs = [
    brandInput,
    modelInput,
    yearInput,
    bauformInput,
    fuelInput,
    variantInput,
    psInput,
    transmissionInput,
    doorsInput,
    modelTypeInput,
    kmStandInput,
  ];

  // Platzhalter und Input-Typen pro Schritt
  const placeholders = [
    "Audi, VW, BMW…",
    "A3, X5…",
    "2020",
    "Limousine, SUV…",
    "Diesel, Benzin…",
    "30 TDI…",
    "116",
    "Automatik, Schaltgetriebe",
    "Z. B. 3 oder 5",
    "Modelvariante",
    "Kilometerstand",
  ];
  const inputTypes = [
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "number",
  ];

  // Step 12 renders the AnmeldenSection in the parent page.
  // Remove the hero entirely so the Anmelden animations start directly under the navbar.
  if (step === 12) return null;

  return (
    <section className="hero">
      <div className="section-container">
        <div className="Auto-bewerten-top">
          <div className="AB-Top-Animation">
            {/* Mobile and desktop sources keep the decorative background lightweight on smaller screens. */}
            <div className="AB-hero-bg" aria-hidden="true">
              <picture>
                <source
                  media="(max-width: 1040px)"
                  srcSet={headerBgMobile.src}
                />
                <img
                  src={headerBgDesktop.src}
                  alt=""
                  className="AB-hero-bg-image"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </div>

            {secondaryCar ? (
              <Image
                src={grausesAutoSecondary}
                alt="Graues Auto"
                className="FNP-gray-car"
                // Matches the actual rendered size of the decorative car to avoid downloading a 1920px wide image on mobile.
                sizes="(max-width: 1040px) 580px, 1024px"
                quality={45}
              />
            ) : (
              <Image
                src={grausesAuto}
                alt="Graues Auto"
                className="AB-gray-car"
                sizes="(max-width: 1040px) 960px, 1200px"
                quality={45}
              />
            )}

            {useCustomImage ? (
              useCustomImage
            ) : (
              <Image
                src={autoWertChart}
                alt="Fahrzeugbewertung mit einem aktuellen Wert von 10,540€ und einem Preisverlauf über ein Jahr."
                className="AB-chart"
                priority
                fetchPriority="high"
                quality={60}
                sizes="(max-width: 1040px) 370px, 320px"
              />
            )}
          </div>

          <div className="AB-Top">
            <div className="Car-Top-wrapper-AB">
              {smallTitle && <h3 className="AB-small-title">{smallTitle}</h3>}
              <h1 className="AB-Top-title">{title}</h1>
              <CarTopInfo
                items={selectedChips}
                textNode={<p className="AB-Top-Info">{description}</p>}
                shouldAnimate={step === 1 || nextStepRef.current === 1}
                animateOut={animateOut}
                animateIn={animateIn}
                hidden={
                  swapInvisible || (isAnimating && !animateOut && !animateIn)
                }
              />

              <div
                className={`AB-Top-Input-wrapper ${
                  animateOut ? "fade-out" : animateIn ? "fade-in" : ""
                } ${swapInvisible ? "is-hidden" : ""}`}
                onAnimationEnd={
                  animateOut ? onFadeOutEnd : animateIn ? onFadeInEnd : null
                }
              >
                <div className="AB-Top-Input-wrapper">
                  <p className="Car-top-FIN">
                    <strong>{headers[step]}</strong>
                    {step > 1 && (
                      <>
                        &nbsp;
                        <span className="step-percentage">
                          ({percentage}% abgeschlossen)
                        </span>
                        <button type="button" className="back-button" onClick={handleBack}>
                          Zurück
                        </button>
                      </>
                    )}
                  </p>
                  <div className="Car-top-input-values">
                    <Input
                      type={inputTypes[step - 1]}
                      value={allInputs[step - 1]}
                      placeholder={placeholders[step - 1]}
                      onFocus={() => {
                        setInputError("");
                        setFilteredForStep(allOptions[step - 1], step);
                      }}
                      valid={
                        !![
                          selectedBrand.id,
                          selectedModel.id,
                          selectedYear.id,
                          selectedBauform.id,
                          selectedFuel.id,
                          selectedVariant.id,
                          selectedPs.id,
                          selectedTransmission.id,
                          selectedDoors.id,
                          selectedModelType.id,
                          selectedKmStand,
                        ][step - 1]
                      }
                      onBlur={() => setFilteredForStep([], step)}
                      onChange={(e) => {
                        setInputError("");
                        handleInput(
                          e.target.value,
                          allOptions[step - 1],
                          allSetInputs[step - 1],
                          (allFiltered) =>
                            setFilteredForStep(allFiltered, step),
                          allSetSelected[step - 1],
                          step,
                        );
                      }}
                      inputMode={
                        step === 3 || step === 11 ? "numeric" : undefined
                      }
                      pattern={step === 3 || step === 11 ? "[0-9]*" : undefined}
                      errorMessage={inputError}
                    />
                    {filteredItems.length > 0 && filteredItemsStep === step && (
                      <div className="Input-dropdown-shell">
                        <ul className="Input-dropdown-values">
                        {filteredItems.map((o, i) => (
                          <li
                            key={i}
                            className="Input-dropdown-value"
                            onMouseDown={() =>
                              handleSelect(
                                o,
                                allSetInputs[step - 1],
                                allSetSelected[step - 1],
                                (items) => setFilteredForStep(items, step),
                              )
                            }
                          >
                            {o.name}
                          </li>
                        ))}
                      </ul>
                      </div>
                    )}
                  </div>
                  <ButtonAnimated
                    onClick={handleNext}
                    className="AB-Top-Button desktop"
                    tooltipPosition="top"
                    text={step === 1 || step >= 11 ? "Auto bewerten" : "Weiter"}
                  />
                  <ButtonAnimated
                    onClick={handleNext}
                    className="AB-Top-Button mobile"
                    tooltipPosition="bottom"
                    text={step === 1 || step >= 11 ? "Auto bewerten" : "Weiter"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
